import posthog from 'posthog-js'
import React, {useState, useEffect, useContext, PropsWithChildren} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {AuthService} from '../../services/Auth'
import {UserDto} from '../../services/User/type'
import {UserService} from '../../services/User'

import {PostHogProvider} from 'posthog-js/react'
import {StrictMode} from 'react'
import {ActivationService} from '../../services/Activation/Activation'
import {deleteCookie, getCookie, getCookieName, setCookie} from '../../common/utils/cookieUtils'
import {LoginByUsernamePasswordBody} from '../../services/Auth/type'
import {useDeviceType} from '../../hooks/mediaQueries'

interface XpertKnowledgeAuthContext {
  logout: () => void
  login: (token: string) => Promise<void>
  loginAdmin: (token: string) => Promise<void>
  loginUsernamePassword: (loginBody: LoginByUsernamePasswordBody) => Promise<void>
  user: UserDto | null
  isLoading: boolean
  isLoginFailed: boolean
  setIsLoginFailed: React.Dispatch<React.SetStateAction<boolean>>
}

const Context = React.createContext<XpertKnowledgeAuthContext>(null as any)

export const XpertKnowledgeAuthContextProvider: React.FC<PropsWithChildren> = ({children}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const deviceType = useDeviceType()
  const [user, setUser] = useState<UserDto | null>(null)
  const [posthogEnabled, setPosthogEnabled] = useState(false)
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isLoginFailed, setIsLoginFailed] = useState<boolean>(false)

  const COOKIE_NAME = getCookieName()

  const logout = () => {
    deleteCookie(COOKIE_NAME)
    setUser(null)
    posthog.capture('logout_completed')
    navigate('/login')
  }

  const login = async (token: string) => {
    try {
      const response = await AuthService.login_xpertKnowledge(token)
      setCookie(COOKIE_NAME, response.data.id)
      const user = await UserService.getMe()
      setUser(user)
      posthog.capture('login_completed')
      navigate('/xpert-knowledge')
    } catch (error) {
      console.error('Login error:', error)
      deleteCookie(COOKIE_NAME)
      navigate('/xpert-knowledge/error')
    }
  }

  const loginAdmin = async (token: string) => {
    try {
      const response = await AuthService.login(token)
      setCookie(COOKIE_NAME, response.data.id)
      const user = await UserService.getMe()
      setUser(user)
      posthog.capture('login_completed')
      navigate('/xpert-knowledge')
    } catch (error) {
      console.error('Login error:', error)
      deleteCookie(COOKIE_NAME)
      navigate('/xpert-knowledge/error')
    }
  }

  const loginUsernamePassword = async (loginBody: LoginByUsernamePasswordBody) => {
    try {
      setIsLoading(true)
      deleteCookie(COOKIE_NAME)
      const response = await AuthService.login_username_password(loginBody)
      setCookie(COOKIE_NAME, response.data.id)
      const user = await UserService.getMe()
      setUser(user)
      posthog.capture('login_completed')
      navigate(redirectUrl || '/xpert-knowledge')
      setRedirectUrl(null)
    } catch (error) {
      console.error('Login error:', error)
      deleteCookie(COOKIE_NAME)
      setIsLoginFailed(true)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const checkUserRole = async () => {
    try {
      const token = getCookie(COOKIE_NAME)
      if (!token) {
        setUser(null)
        return
      }

      const response = await UserService.getMe()
      setUser(response)
    } catch (error) {
      console.error('Error fetching user role:', error)
      navigate('/xpert-knowledge/error')
    }
  }

  const getActivationConfig = async () => {
    try {
      const response = await ActivationService.getConfig()
      setPosthogEnabled(response.posthogEnabled)
    } catch (error) {
      console.error('Error fetching activation config:', error)
      navigate('/xpert-knowledge/error')
    }
  }

  useEffect(() => {
    if (
      location.pathname.includes('/xpert-knowledge/sso') ||
      location.pathname.includes('/xpert-knowledge/error') ||
      location.pathname.includes('/xpert-knowledge/tutorial/not-found') ||
      location.pathname.includes('/rag/sso') ||
      location.pathname.includes('/login') ||
      location.pathname.includes('/public')
    )
      return

    const storedToken = getCookie(COOKIE_NAME)
    if (!storedToken) {
      if (location.pathname != 'xpert-knowledge/error') {
        setRedirectUrl(location.pathname)
      }
      navigate('/login')
      return
    }

    checkUserRole()
    getActivationConfig()
    if (location.pathname === '/xpert-knowledge/') {
      navigate('/xpert-knowledge')
    }
  }, [location.pathname])

  posthog.register({
    user_role: user?.role ?? 'consumer',
    device_type: deviceType,
  })

  return (
    <Context.Provider value={{logout, login, loginAdmin, loginUsernamePassword, user, isLoading, isLoginFailed, setIsLoginFailed}}>
      {posthogEnabled && window.location.origin !== 'http://localhost:3000' ? (
        <StrictMode>
          <PostHogProvider
            apiKey="phc_jxx3uhjMD9pIdRMEYb9mU5n0QkARZ6mxS5cqOIYbsP5"
            options={{
              api_host: 'https://eu.i.posthog.com',
              capture_pageview: 'history_change',
              autocapture: false,
              capture_performance: {web_vitals: false},
              session_recording: {
                maskTextSelector: '[data-attr-masked="true"]',
              },
            }}
          >
            {children}
          </PostHogProvider>
        </StrictMode>
      ) : (
        children
      )}
    </Context.Provider>
  )
}

export const useXpertKnowledgeAuthContext = () => useContext(Context)
